import { Chess } from 'chess.js';
import type { EngineLine, SearchOptions } from './engine.ts';

export type BotLevel = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface BotProfile {
  level: BotLevel;
  /** Approximate playing strength, shown to the user as a guide. */
  rating: number;
  /** The animal character kids see for this level. */
  emoji: string;
  /** Soft background behind the character. */
  accent: string;
  search: Omit<SearchOptions, 'fen'>;
  /** Chance of playing a random legal move instead of an engine move. */
  blunderRate: number;
  /** Among the engine's candidate lines, how far (cp) below best a move may be. */
  slackCp: number;
}

export const BOTS: readonly BotProfile[] = [
  { level: 1, rating: 400, emoji: '🐛', accent: '#d4f5c9', search: { skill: 0, depth: 1, movetime: 200, multiPv: 6 }, blunderRate: 0.4, slackCp: 400 },
  { level: 2, rating: 700, emoji: '🐢', accent: '#c9f0e6', search: { skill: 0, depth: 2, movetime: 250, multiPv: 5 }, blunderRate: 0.22, slackCp: 250 },
  { level: 3, rating: 1000, emoji: '🐰', accent: '#ffdcec', search: { skill: 2, depth: 4, movetime: 300, multiPv: 4 }, blunderRate: 0.1, slackCp: 150 },
  { level: 4, rating: 1300, emoji: '🦊', accent: '#ffe0c4', search: { skill: 5, depth: 6, movetime: 400, multiPv: 3 }, blunderRate: 0.04, slackCp: 90 },
  { level: 5, rating: 1600, emoji: '🦉', accent: '#e6dcff', search: { skill: 9, depth: 8, movetime: 500, multiPv: 2 }, blunderRate: 0, slackCp: 50 },
  { level: 6, rating: 1900, emoji: '🐺', accent: '#d6e1f0', search: { skill: 13, depth: 11, movetime: 700 }, blunderRate: 0, slackCp: 0 },
  { level: 7, rating: 2200, emoji: '🦅', accent: '#cfe6ff', search: { skill: 17, depth: 14, movetime: 1000 }, blunderRate: 0, slackCp: 0 },
  { level: 8, rating: 2500, emoji: '🐉', accent: '#ffd2d2', search: { skill: 20, depth: 18, movetime: 1500 }, blunderRate: 0, slackCp: 0 },
];

export function botByLevel(level: number): BotProfile {
  return BOTS.find((b) => b.level === level) ?? BOTS[2];
}

/**
 * Turn the engine's candidates into a human-like choice for weaker levels:
 * occasionally a random move, otherwise a random pick among "good enough" lines.
 */
export function chooseBotMove(
  bot: BotProfile,
  fen: string,
  bestMove: string,
  lines: readonly EngineLine[],
  random: () => number = Math.random,
): string {
  // Never throw away a forced mate the engine found.
  const best = lines[0]?.score ?? 0;
  if (best >= 100_000) return bestMove;

  if (bot.blunderRate > 0 && random() < bot.blunderRate) {
    const moves = new Chess(fen).moves({ verbose: true });
    const pick = moves[Math.floor(random() * moves.length)];
    if (pick) return pick.from + pick.to + (pick.promotion ?? '');
  }

  if (bot.slackCp > 0 && lines.length > 1) {
    const candidates = lines.filter((l) => best - l.score <= bot.slackCp && Math.abs(l.score) < 100_000);
    if (candidates.length > 0) return candidates[Math.floor(random() * candidates.length)].move;
  }

  return bestMove;
}

/** Engine settings used for hints, independent of the opponent's level. */
export const HINT_SEARCH: Omit<SearchOptions, 'fen'> = { skill: 20, depth: 14, movetime: 800 };
